import { useQueries } from '@tanstack/react-query'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { axiosInstance } from '@/lib/axios'
import type { DriverModel } from '@/app/models'

type Props = {
  companyId: string | number | undefined
  driverIds: Array<string | number>
  withPersonalData?: boolean
}

/**
 * Fetches full driver records for an arbitrary set of ids in parallel via
 * `useQueries`. Each driver is cached individually under the same key used
 * by `useQueryDriver`, so a driver already in cache (e.g. from a view bar
 * on a previous page) is returned instantly.
 *
 * Used by flows that need full `DriverModel`s for selected drivers (IMI
 * export etc.) without having to load every driver in the company upfront.
 */
export function useQueryDriversByIds({
  companyId,
  driverIds,
  withPersonalData = true,
}: Props) {
  const results = useQueries({
    queries: driverIds.map((driverId) => ({
      queryKey: [
        'driver',
        String(companyId),
        String(driverId),
        withPersonalData,
      ],
      enabled: !!companyId && driverId != null,
      staleTime: MINUTE_IN_MS * 5,
      queryFn: async (): Promise<DriverModel | null> => {
        const res = await axiosInstance.get(
          `api/${companyId}/driver/${driverId}?withPersonalData=${withPersonalData}`,
        )
        return res.data
      },
    })),
  })

  const drivers = results
    .map((r) => r.data)
    .filter((d): d is DriverModel => !!d)
  const isLoading = results.some((r) => r.isLoading)

  return { drivers, isLoading, results }
}
